/**
 * API client. No state library — this is `fetch` plus zod parsing, called from
 * react-router loaders and actions.
 *
 * Responses are parsed through the same contracts the server validates with, so
 * a shape drift shows up as a parse error at the boundary rather than as
 * undefined three components deep.
 */

import type { ZodTypeAny, infer as ZodInfer } from 'zod'
import {
  leadListResult,
  lead as leadSchema,
  type CreateLeadInput,
  type Lead,
  type LeadListQuery,
  type LeadListResult,
  type UpdateLeadInput,
} from '@erp/contracts'
import {
  dealBoard,
  dealListResult,
  deal as dealSchema,
  type ChangeDealStatusInput,
  type CreateDealInput,
  type Deal,
  type DealBoard,
  type DealBoardQuery,
  type DealListQuery,
  type DealListResult,
  type UpdateDealInput,
} from '@erp/contracts'
import {
  contactListResult,
  contact as contactSchema,
  organizationListResult,
  organization as organizationSchema,
  type Contact,
  type ContactListQuery,
  type ContactListResult,
  type CreateContactInput,
  type CreateOrganizationInput,
  type Organization,
  type OrganizationListQuery,
  type OrganizationListResult,
  type UpdateContactInput,
  type UpdateOrganizationInput,
} from '@erp/contracts'
import {
  assignmentList,
  noteListResult,
  note as noteSchema,
  taskListResult,
  task as taskSchema,
  timelineResult,
  type CreateNoteInput,
  type CreateTaskInput,
  type Assignment,
  type Note,
  type NoteListQuery,
  type NoteListResult,
  type SubjectType,
  type Task,
  type TaskListQuery,
  type TaskListResult,
  type TimelineResult,
  type UpdateNoteInput,
  type UpdateTaskInput,
} from '@erp/contracts'
import {
  fieldRegistry,
  savedView,
  type FieldRegistry,
  type SavedView,
  type UpsertSavedViewInput,
  type ViewEntity,
} from '@erp/contracts'
import { z } from 'zod'
import {
  lookupItem,
  lookups as lookupsSchema,
  type CreateLookupInput,
  type LookupItem,
  type Lookups,
} from '@erp/contracts'

/** Adjust to however the app reaches its API (base path, proxy, or absolute). */
const BASE = '/api'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly issues: Record<string, string[]> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T extends ZodTypeAny>(
  schema: T,
  path: string,
  init?: RequestInit,
): Promise<ZodInfer<T>> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    credentials: 'include',
  })

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null)
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : response.statusText
    const issues =
      typeof body === 'object' && body !== null && 'issues' in body
        ? ((body as { issues: Record<string, string[]> }).issues ?? {})
        : {}
    throw new ApiError(response.status, message, issues)
  }

  return schema.parse(await response.json()) as ZodInfer<T>
}

/** For endpoints with no meaningful response body. */
async function command(path: string, init: RequestInit): Promise<void> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    credentials: 'include',
  })
  if (!response.ok) {
    const parsed: unknown = await response.json().catch(() => null)
    const message =
      typeof parsed === 'object' && parsed !== null && 'message' in parsed
        ? String((parsed as { message: unknown }).message)
        : response.statusText
    throw new ApiError(response.status, message)
  }
}

function qs(query: Record<string, unknown>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item))
    } else {
      params.set(key, String(value))
    }
  }
  const serialised = params.toString()
  return serialised.length > 0 ? `?${serialised}` : ''
}

const body = (value: unknown): RequestInit => ({ body: JSON.stringify(value) })

/**
 * `filters` is an array of objects, which `qs` would render as `[object Object]`
 * — it JSON-encodes here instead, matching the `jsonParam` the contracts use to
 * read it back. Every other list param is a scalar or an array of scalars and
 * passes through untouched.
 */
function listParams<T extends { filters?: readonly unknown[] | undefined }>(
  query: T,
): Record<string, unknown> {
  const { filters, ...rest } = query
  return filters && filters.length > 0 ? { ...rest, filters: JSON.stringify(filters) } : rest
}

export const api = {
  leads: {
    list: (query: Partial<LeadListQuery>): Promise<LeadListResult> =>
      request(leadListResult, `/crm/leads${qs(listParams(query))}`),
    get: (id: string): Promise<Lead> => request(leadSchema, `/crm/leads/${id}`),
    create: (input: CreateLeadInput): Promise<Lead> =>
      request(leadSchema, '/crm/leads', { method: 'POST', ...body(input) }),
    update: (id: string, input: UpdateLeadInput): Promise<Lead> =>
      request(leadSchema, `/crm/leads/${id}`, { method: 'PATCH', ...body(input) }),
    scheduleFollowUp: (id: string, at: string): Promise<Lead> =>
      request(leadSchema, `/crm/leads/${id}/follow-up`, { method: 'POST', ...body({ at }) }),
    convert: (id: string, input: unknown): Promise<Deal> =>
      request(dealSchema, `/crm/leads/${id}/convert`, { method: 'POST', ...body(input) }),
    timeline: (id: string, query: { limit?: number; cursor?: string } = {}): Promise<TimelineResult> =>
      request(timelineResult, `/crm/leads/${id}/timeline${qs(query)}`),
  },

  deals: {
    list: (query: Partial<DealListQuery>): Promise<DealListResult> =>
      request(dealListResult, `/crm/deals${qs(listParams(query))}`),
    board: (query: Partial<DealBoardQuery> = {}): Promise<DealBoard> =>
      request(dealBoard, `/crm/deals/board${qs(listParams(query))}`),
    get: (id: string): Promise<Deal> => request(dealSchema, `/crm/deals/${id}`),
    create: (input: CreateDealInput): Promise<Deal> =>
      request(dealSchema, '/crm/deals', { method: 'POST', ...body(input) }),
    update: (id: string, input: UpdateDealInput): Promise<Deal> =>
      request(dealSchema, `/crm/deals/${id}`, { method: 'PATCH', ...body(input) }),
    changeStatus: (id: string, input: ChangeDealStatusInput): Promise<Deal> =>
      request(dealSchema, `/crm/deals/${id}/status`, { method: 'POST', ...body(input) }),
    timeline: (id: string, query: { limit?: number; cursor?: string } = {}): Promise<TimelineResult> =>
      request(timelineResult, `/crm/deals/${id}/timeline${qs(query)}`),
  },

  organizations: {
    list: (query: Partial<OrganizationListQuery>): Promise<OrganizationListResult> =>
      request(organizationListResult, `/crm/organizations${qs(listParams(query))}`),
    get: (id: string): Promise<Organization> =>
      request(organizationSchema, `/crm/organizations/${id}`),
    create: (input: CreateOrganizationInput): Promise<Organization> =>
      request(organizationSchema, '/crm/organizations', { method: 'POST', ...body(input) }),
    update: (id: string, input: UpdateOrganizationInput): Promise<Organization> =>
      request(organizationSchema, `/crm/organizations/${id}`, { method: 'PATCH', ...body(input) }),
    archive: (id: string): Promise<void> => command(`/crm/organizations/${id}`, { method: "DELETE" }),
    timeline: (id: string, query: { limit?: number; cursor?: string } = {}): Promise<TimelineResult> =>
      request(timelineResult, `/crm/organizations/${id}/timeline${qs(query)}`),
  },

  contacts: {
    list: (query: Partial<ContactListQuery>): Promise<ContactListResult> =>
      request(contactListResult, `/crm/contacts${qs(listParams(query))}`),
    get: (id: string): Promise<Contact> => request(contactSchema, `/crm/contacts/${id}`),
    create: (input: CreateContactInput): Promise<Contact> =>
      request(contactSchema, '/crm/contacts', { method: 'POST', ...body(input) }),
    update: (id: string, input: UpdateContactInput): Promise<Contact> =>
      request(contactSchema, `/crm/contacts/${id}`, { method: 'PATCH', ...body(input) }),
    archive: (id: string): Promise<void> => command(`/crm/contacts/${id}`, { method: "DELETE" }),
    timeline: (id: string, query: { limit?: number; cursor?: string } = {}): Promise<TimelineResult> =>
      request(timelineResult, `/crm/contacts/${id}/timeline${qs(query)}`),
  },

  tasks: {
    list: (query: Partial<TaskListQuery>): Promise<TaskListResult> =>
      request(taskListResult, `/crm/tasks${qs(query)}`),
    forSubject: (type: SubjectType, id: string): Promise<TaskListResult> =>
      request(taskListResult, `/crm/tasks${qs({ subjectType: type, subjectId: id, openOnly: false })}`),
    create: (input: CreateTaskInput): Promise<Task> =>
      request(taskSchema, '/crm/tasks', { method: 'POST', ...body(input) }),
    update: (id: string, input: UpdateTaskInput): Promise<Task> =>
      request(taskSchema, `/crm/tasks/${id}`, { method: 'PATCH', ...body(input) }),
    remove: (id: string): Promise<void> => command(`/crm/tasks/${id}`, { method: "DELETE" }),
    restore: (id: string): Promise<Task> =>
      request(taskSchema, `/crm/tasks/${id}/restore`, { method: 'POST' }),
  },

  notes: {
    list: (query: Partial<NoteListQuery>): Promise<NoteListResult> =>
      request(noteListResult, `/crm/notes${qs(query)}`),
    forSubject: (type: SubjectType, id: string): Promise<NoteListResult> =>
      request(noteListResult, `/crm/notes${qs({ subjectType: type, subjectId: id })}`),
    /** Deleted notes, for the restore panel. */
    deletedFor: (type: SubjectType, id: string): Promise<NoteListResult> =>
      request(
        noteListResult,
        `/crm/notes${qs({ subjectType: type, subjectId: id, includeDeleted: true })}`,
      ),
    restore: (id: string): Promise<Note> =>
      request(noteSchema, `/crm/notes/${id}/restore`, { method: 'POST' }),
    create: (input: CreateNoteInput): Promise<Note> =>
      request(noteSchema, '/crm/notes', { method: 'POST', ...body(input) }),
    update: (id: string, input: UpdateNoteInput): Promise<Note> =>
      request(noteSchema, `/crm/notes/${id}`, { method: 'PATCH', ...body(input) }),
    remove: (id: string): Promise<void> => command(`/crm/notes/${id}`, { method: "DELETE" }),
  },

  views: {
    forEntity: (entity: ViewEntity): Promise<SavedView[]> =>
      request(z.array(savedView), `/crm/views${qs({ entity })}`),
    get: (id: string): Promise<SavedView> => request(savedView, `/crm/views/${id}`),
    create: (input: UpsertSavedViewInput): Promise<SavedView> =>
      request(savedView, '/crm/views', { method: 'POST', ...body(input) }),
    update: (id: string, input: UpsertSavedViewInput): Promise<SavedView> =>
      request(savedView, `/crm/views/${id}`, { method: 'PATCH', ...body(input) }),
    remove: (id: string): Promise<void> => command(`/crm/views/${id}`, { method: 'DELETE' }),
  },

  /** All reference data in one request; the layout route loads it once. */
  lookups: (): Promise<Lookups> => request(lookupsSchema, '/crm/lookups'),

  /** Extends a descriptive lookup; idempotent if the name already exists. */
  createLookup: (input: CreateLookupInput): Promise<LookupItem> =>
    request(lookupItem, '/crm/lookups', { method: 'POST', ...body(input) }),

  /** One endpoint for every subject type — the table is polymorphic, the operation identical. */
  assignments: {
    forSubject: (type: SubjectType, id: string): Promise<Assignment[]> =>
      request(assignmentList, `/crm/assignments${qs({ subjectType: type, subjectId: id })}`),
    /** Assignees for a whole page of records in one request. */
    forSubjects: (type: SubjectType, ids: readonly string[]): Promise<Assignment[]> =>
      ids.length === 0
        ? Promise.resolve([])
        : request(assignmentList, `/crm/assignments${qs({ subjectType: type, subjectId: ids })}`),
    set: (subject: { type: SubjectType; id: string }, userIds: string[]): Promise<Assignment[]> =>
      request(assignmentList, '/crm/assignments', {
        method: 'POST',
        ...body({ subject, userIds }),
      }),
  },

  registry: (entity: ViewEntity): Promise<FieldRegistry> =>
    request(fieldRegistry, `/crm/registry/${entity}`),
}
